import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { auth } from '../core/auth.js';
import { icon } from '../core/icons.js';
import { t, getLocale } from '../core/i18n.js';
import { formatDateTime, formatMoney } from '../core/format.js';
import { toast } from '../components/Toast.js';
import { showModal } from '../components/Modal.js';
import { showFormError, clearFormError, fieldFromApiError } from '../core/formError.js';
import { SignaturePad } from '../components/SignaturePad.js';

export class HandoverEditView extends Component {
  constructor(ctx) {
    super(ctx);
    this.id = ctx.params.id;
    this.state = { h: null, error: null };
    this._pads = { LANDLORD: null, TENANT: null };
    this._blobUrls = [];
    this._prevSignedKey = '';
  }

  // Fetches a protected asset and resolves to {url, mime}. Tracked for cleanup.
  async _loadProtectedAsset(path) {
    try {
      const blob = await api.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      this._blobUrls.push(url);
      return { url, mime: blob.type || '' };
    } catch (_) {
      return null;
    }
  }

  _revokeBlobUrls() {
    for (const url of this._blobUrls) {
      try { URL.revokeObjectURL(url); } catch (_) {}
    }
    this._blobUrls = [];
  }

  template() {
    const h = this.state.h;
    if (h === null) {
      return `<section class="stack">${this.state.error
        ? `<div class="error-banner">${esc(this.state.error)}</div>`
        : `<div class="muted">${esc(t('common.loading'))}</div>`}</section>`;
    }
    const isLandlord = h.viewer_role === 'LANDLORD';
    const isDraft = h.status === 'DRAFT';
    const tenancyHref = `#/tenancies/${esc(h.tenancy_id)}`;
    const ribbonText = `${t('handover.ribbon.protocol')} · ${t('handover.statuses.' + h.status)}`;
    return `
      <section class="handover-edit stack">
        <a href="${tenancyHref}" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('handover.back_to_handover'))}</span></a>
        <div class="handover-edit-body ${isDraft ? 'is-draft' : ''}">
          ${isDraft ? `<aside class="handover-ribbon" aria-hidden="true"><span>${esc(ribbonText)}</span></aside>` : ''}
          <div class="handover-edit-main stack">
            <header class="handover-edit-header stack-2">
              <div class="handover-edit-title-row">
                <h2 style="margin:0">${esc(t('handover.types.' + h.type))}</h2>
                <button class="btn btn-outline btn-sm" data-action="handover-pdf">${icon('download', { size: 'sm' })}<span>${esc(t('handover.download_pdf'))}</span></button>
              </div>
              ${h.tenancy_summary ? `<span class="faint">${esc(h.tenancy_summary.tenant_name || '')} · ${esc(h.tenancy_summary.start_date || '')}${h.tenancy_summary.end_date ? ' → ' + esc(h.tenancy_summary.end_date) : ''}</span>` : ''}
              ${this._notesHtml(h, isLandlord, isDraft)}
            </header>

            ${this._numberedSection(1, t('handover.sections.keys'), this._keysBody(h, isLandlord, isDraft), { editHint: isLandlord && isDraft })}
            ${this._numberedSection(2, t('handover.sections.meters'), this._metersBody(h, isLandlord, isDraft), { editHint: isLandlord && isDraft })}
            ${this._numberedSection(3, t('handover.sections.rooms'), this._roomsBody(h, isLandlord, isDraft), { editHint: isLandlord && isDraft })}
            ${this._numberedSection(4, t('handover.sections.deposit'), this._depositBody(h, isLandlord, isDraft), { editHint: isLandlord && isDraft })}
            ${this._numberedSection(5, t('handover.sections.signatures'), this._signBody(h, isLandlord, isDraft))}

            ${this._footerActions(h, isLandlord, isDraft)}
          </div>
        </div>
      </section>
    `;
  }

  _numberedSection(n, title, bodyHtml, opts = {}) {
    const hint = opts.editHint
      ? `<span class="section-edit-hint" aria-hidden="true">[${esc(t('handover.section_action.edit'))}]</span>`
      : '';
    return `
      <section class="handover-section">
        <header class="handover-section-head">
          <span class="handover-section-num" aria-hidden="true">${n}</span>
          <h3 class="handover-section-title">${esc(title)}</h3>
          ${hint}
        </header>
        <div class="handover-section-body">${bodyHtml}</div>
      </section>
    `;
  }

  _footerActions(h, isLandlord, isDraft) {
    const buttons = [];
    if (isLandlord && isDraft && h.landlord_signed_at && h.tenant_signed_at) {
      buttons.push(`<button class="btn btn-primary btn-block" data-action="handover-complete">${esc(t('handover.complete'))}</button>`);
    }
    if (isDraft) {
      buttons.push(`<button class="btn btn-outline btn-block" data-action="handover-cancel-edit">${esc(t('handover.cancel_editing'))}</button>`);
    }
    if (isLandlord && isDraft) {
      buttons.push(`<button class="btn btn-danger-outline btn-block" data-action="handover-delete">${esc(t('handover.delete'))}</button>`);
    }
    if (buttons.length === 0) return '';
    return `<div class="handover-footer-actions stack-2">${buttons.join('')}</div>`;
  }

  _notesHtml(h, isLandlord, isDraft) {
    if (!isLandlord || !isDraft) {
      return h.notes ? `<p class="muted" style="margin:0;white-space:pre-wrap">${esc(h.notes)}</p>` : '';
    }
    return `
      <form class="stack" data-form="notes">
        <div data-form-error></div>
        <div class="field">
          <label>${esc(t('handover.notes'))}</label>
          <textarea name="notes" class="textarea" rows="2">${esc(h.notes || '')}</textarea>
        </div>
        <div class="row" style="gap:var(--space-2)"><button type="submit" class="btn btn-ghost">${esc(t('common.save'))}</button></div>
      </form>
    `;
  }

  async _load() {
    try {
      const h = await api.get(`/handovers/${this.id}`);
      this._maybeNotifyInvalidation(h);
      this._revokeBlobUrls();
      this.setState({ h, error: null });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('handover.errors.load') });
    }
  }

  // Compare prior signed timestamps with the freshly loaded handover and surface
  // a toast when both were present before and both are now cleared.
  _maybeNotifyInvalidation(next) {
    const prev = this.state.h;
    const prevKey = prev ? `${prev.landlord_signed_at || ''}|${prev.tenant_signed_at || ''}` : '';
    const hadAny = prev && (prev.landlord_signed_at || prev.tenant_signed_at);
    const hasAny = next && (next.landlord_signed_at || next.tenant_signed_at);
    if (hadAny && !hasAny && prevKey !== this._prevSignedKey) {
      toast.info(t('handover.signatures.invalidated'));
    }
    this._prevSignedKey = `${next.landlord_signed_at || ''}|${next.tenant_signed_at || ''}`;
  }

  mount() {
    if (this.state.h === null) { this._load(); return; }
    this._bindHeader();
    this._bindKeys();
    this._bindRooms();
    this._bindMeters();
    this._bindDeposit();
    this._bindSignatures();
    this._hydrateProtectedAssets();
  }

  destroy() {
    if (this._pads.LANDLORD) { this._pads.LANDLORD.destroy(); this._pads.LANDLORD = null; }
    if (this._pads.TENANT) { this._pads.TENANT.destroy(); this._pads.TENANT = null; }
    this._revokeBlobUrls();
    super.destroy?.();
  }

  // Resolves all <img data-protected-src> placeholders by fetching the asset
  // through the authenticated API client. Propagates the resulting blob URL and
  // MIME type to a surrounding lightbox trigger so click handlers can reuse it
  // without an extra round-trip, and swaps the thumbnail for a PDF badge when
  // the asset is not an image.
  async _hydrateProtectedAssets() {
    const imgs = Array.from(this.el.querySelectorAll('img[data-protected-src]'));
    await Promise.all(imgs.map(async (img) => {
      const path = img.getAttribute('data-protected-src');
      const res = await this._loadProtectedAsset(path);
      if (!res) return;
      img.src = res.url;
      const trigger = img.closest('[data-action="defect-photo-view"]');
      if (trigger) {
        trigger.dataset.blobUrl = res.url;
        trigger.dataset.mime = res.mime || '';
        if (res.mime && !res.mime.startsWith('image/')) {
          trigger.classList.add('is-pdf');
          img.removeAttribute('src');
        }
      }
    }));
  }

  _openPhotoLightbox(url, mime, title) {
    if (!url) return;
    const isImage = !mime || mime.startsWith('image/');
    const body = isImage
      ? `<img src="${esc(url)}" alt="${esc(title)}" class="lightbox-image" />`
      : `<iframe src="${esc(url)}" class="lightbox-frame" title="${esc(title)}"></iframe>`;
    showModal({
      title,
      body,
      actions: [
        { label: t('handover.defects.open_in_tab'), variant: 'ghost', onClick: () => { window.open(url, '_blank', 'noopener'); return false; } },
        { label: t('common.close'), variant: 'primary' },
      ],
    });
  }

  _bindHeader() {
    const del = this.el.querySelector('[data-action="handover-delete"]');
    if (del) del.addEventListener('click', () => this._delete());
    const cmp = this.el.querySelector('[data-action="handover-complete"]');
    if (cmp) cmp.addEventListener('click', () => this._complete());
    const cancel = this.el.querySelector('[data-action="handover-cancel-edit"]');
    if (cancel) cancel.addEventListener('click', () => { window.location.hash = `#/tenancies/${this.state.h.tenancy_id}`; });
    const pdfBtn = this.el.querySelector('[data-action="handover-pdf"]');
    if (pdfBtn) pdfBtn.addEventListener('click', () => this._downloadPdf(pdfBtn));
    const notesForm = this.el.querySelector('[data-form="notes"]');
    if (notesForm) notesForm.addEventListener('submit', (ev) => {
      ev.preventDefault();
      clearFormError(notesForm);
      this._patchHeader(notesForm, { notes: notesForm.querySelector('textarea[name="notes"]').value });
    });
  }

  async _delete() {
    if (!confirm(t('handover.delete_confirm'))) return;
    try {
      await api.delete(`/handovers/${this.id}`);
      window.location.hash = `#/tenancies/${this.state.h.tenancy_id}`;
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('handover.errors.generic'));
    }
  }

  async _complete() {
    try {
      const h = await api.post(`/handovers/${this.id}/complete`);
      this.setState({ h, error: null });
      toast.success(t('handover.completed'));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('handover.errors.generic'));
    }
  }

  async _downloadPdf(btn) {
    if (btn.disabled) return;
    btn.disabled = true;
    const label = btn.querySelector('span');
    const original = label ? label.textContent : '';
    if (label) label.textContent = t('handover.download_in_progress');
    try {
      const blob = await api.get(
        `/handovers/${encodeURIComponent(this.id)}/pdf?lang=${encodeURIComponent(getLocale())}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `handover-${this.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('handover.download_error'));
    } finally {
      if (label) label.textContent = original;
      btn.disabled = false;
    }
  }

  async _patchHeader(form, body) {
    try {
      const h = await api.patch(`/handovers/${this.id}`, body);
      this.setState({ h });
      toast.success(t('handover.saved'));
    } catch (err) {
      const field = fieldFromApiError(err, ['notes', 'expected_deposit', 'deposit_confirmed_amount']);
      showFormError(form, err instanceof ApiError ? err.message : t('handover.errors.generic'), { field });
    }
  }

  // ---------- keys ----------

  _keysBody(h, isLandlord, isDraft) {
    const list = Array.isArray(h.keys) ? h.keys : [];
    const canEdit = isLandlord && isDraft;
    const rows = list.length === 0
      ? `<div class="empty">${esc(t('handover.keys.empty'))}</div>`
      : `<div class="key-rows">${list.map((k) => this._keyRowHtml(k, canEdit)).join('')}</div>`;
    const adder = canEdit ? `
      <form class="key-add" data-form="add-key">
        <div data-form-error></div>
        <div class="row" style="gap:var(--space-2)">
          <input class="input grow" name="label" placeholder="${esc(t('handover.keys.placeholder'))}" />
          <input class="input" style="max-width:5rem" name="quantity" type="number" min="1" value="1" aria-label="${esc(t('handover.keys.qty_label'))}" />
        </div>
        <button type="submit" class="btn btn-outline btn-block">${icon('plus', { size: 'sm' })}<span>${esc(t('handover.keys.add'))}</span></button>
      </form>` : '';
    return `${rows}${adder}`;
  }

  _keyRowHtml(k, canEdit) {
    const qty = Number(k.quantity || 0);
    return `
      <div class="key-row" data-key-id="${esc(k.id)}">
        ${icon('key', { class: 'key-row-icon' })}
        <span class="key-row-label">${esc(k.label)}</span>
        <span class="key-row-qty">${esc(t('handover.keys.qty', { n: qty }))}</span>
        ${canEdit ? `
          <div class="key-row-controls">
            <button type="button" class="btn btn-ghost btn-icon" data-action="key-qty" data-id="${esc(k.id)}" data-delta="-1" aria-label="${esc(t('handover.keys.decrement'))}" ${qty <= 1 ? 'disabled' : ''}>${icon('arrow-down', { size: 'sm' })}</button>
            <button type="button" class="btn btn-ghost btn-icon" data-action="key-qty" data-id="${esc(k.id)}" data-delta="1" aria-label="${esc(t('handover.keys.increment'))}">${icon('plus', { size: 'sm' })}</button>
            <button type="button" class="btn btn-ghost btn-icon" data-action="key-delete" data-id="${esc(k.id)}" aria-label="${esc(t('common.delete'))}">${icon('trash', { size: 'sm' })}</button>
          </div>` : ''}
      </div>`;
  }

  _bindKeys() {
    this.el.querySelectorAll('[data-action="key-delete"]').forEach((b) => b.addEventListener('click', () => this._deleteKey(b.getAttribute('data-id'))));
    this.el.querySelectorAll('[data-action="key-qty"]').forEach((b) => b.addEventListener('click', () => {
      const id = b.getAttribute('data-id');
      const delta = parseInt(b.getAttribute('data-delta'), 10) || 0;
      this._adjustKeyQty(id, delta);
    }));
    const f = this.el.querySelector('[data-form="add-key"]');
    if (f) f.addEventListener('submit', (ev) => {
      ev.preventDefault();
      clearFormError(f);
      const label = (f.querySelector('input[name="label"]').value || '').trim();
      const quantity = parseInt(f.querySelector('input[name="quantity"]').value, 10) || 1;
      this._addKey(f, { label, quantity });
    });
  }

  async _addKey(form, body) {
    try {
      await api.post(`/handovers/${this.id}/keys`, body);
      form.querySelector('input[name="label"]').value = '';
      form.querySelector('input[name="quantity"]').value = '1';
      await this._load();
    } catch (err) {
      const field = fieldFromApiError(err, ['label', 'quantity']);
      showFormError(form, err instanceof ApiError ? err.message : t('handover.errors.generic'), { field });
    }
  }

  async _adjustKeyQty(id, delta) {
    const list = Array.isArray(this.state.h?.keys) ? this.state.h.keys : [];
    const cur = list.find((k) => k.id === id);
    if (!cur) return;
    const next = Math.max(1, Math.min(999, Number(cur.quantity || 1) + delta));
    if (next === Number(cur.quantity)) return;
    try { await api.patch(`/handover-keys/${id}`, { quantity: next }); await this._load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : t('handover.errors.generic')); }
  }

  async _deleteKey(id) {
    if (!confirm(t('handover.keys.delete_confirm'))) return;
    try { await api.delete(`/handover-keys/${id}`); await this._load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : t('handover.errors.generic')); }
  }

  // ---------- rooms + defects ----------

  _roomsBody(h, isLandlord, isDraft) {
    const rooms = Array.isArray(h.rooms) ? h.rooms : [];
    const body = rooms.length === 0
      ? `<div class="empty">${esc(t('handover.rooms.empty'))}</div>`
      : `<div class="room-rows">${rooms.map((r) => this._roomCard(r, isLandlord, isDraft)).join('')}</div>`;
    const addForm = isLandlord && isDraft ? `
      <form class="row" style="gap:var(--space-2)" data-form="add-room">
        <div data-form-error></div>
        <input class="input grow" name="name" placeholder="${esc(t('handover.rooms.placeholder'))}" aria-label="${esc(t('handover.rooms.name_label'))}" />
        <button type="submit" class="btn btn-outline">${icon('plus', { size: 'sm' })}<span>${esc(t('handover.rooms.add'))}</span></button>
      </form>` : '';
    return `${body}${addForm}`;
  }

  _roomCard(r, isLandlord, isDraft) {
    const defects = Array.isArray(r.defects) ? r.defects : [];
    const photos = Array.isArray(r.photos) ? r.photos : [];
    const defectCount = defects.length;
    const badgeHtml = defectCount > 0
      ? `<span class="defect-badge">${esc(t('handover.defects.count_badge', { n: defectCount }))}</span>`
      : `<span class="defect-badge defect-badge--ok">${esc(t('handover.defects.no_defects'))}</span>`;
    const photoBadge = photos.length > 0
      ? `<span class="defect-badge defect-badge--ok">${esc(t('handover.room_photos.count_badge', { n: photos.length }))}</span>`
      : '';
    const defectRows = defects.length === 0
      ? ''
      : `<div class="stack-2">${defects.map((d) => `
          <div class="row" style="gap:var(--space-2);align-items:center">
            <div class="stack-2 grow">
              <span>${esc(d.description)}</span>
              ${d.photo_path ? `
                <button type="button" class="defect-photo-trigger" data-action="defect-photo-view" aria-label="${esc(t('handover.defects.view_photo'))}" title="${esc(t('handover.defects.view_photo'))}">
                  <img alt="${esc(t('handover.defects.photo'))}" data-protected-src="/handover-defects/${esc(d.id)}/photo" />
                  <span class="defect-photo-trigger-overlay">${icon('eye', { size: 'sm' })}</span>
                </button>` : ''}
            </div>
            ${isLandlord && isDraft ? `
              <label class="btn btn-ghost" title="${esc(t('handover.defects.upload_photo'))}">
                ${icon('upload', { size: 'sm' })}
                <input type="file" accept="image/*,application/pdf" hidden data-defect-photo="${esc(d.id)}" />
              </label>
              <button class="btn btn-ghost" data-action="defect-delete" data-id="${esc(d.id)}" aria-label="${esc(t('common.delete'))}">${icon('trash', { size: 'sm' })}</button>
            ` : ''}
          </div>`).join('')}</div>`;
    const addDefectForm = isLandlord && isDraft ? `
      <form class="row" style="gap:var(--space-2)" data-form="add-defect" data-room-id="${esc(r.id)}">
        <div data-form-error></div>
        <div class="field grow"><input class="input" name="description" placeholder="${esc(t('handover.defects.placeholder'))}" /></div>
        <button type="submit" class="btn btn-ghost">${icon('plus', { size: 'sm' })}<span>${esc(t('handover.defects.add'))}</span></button>
      </form>` : '';
    const photosBlock = this._roomPhotosBlock(r, photos, isLandlord, isDraft);
    const isOpen = defectCount > 0 || photos.length > 0;
    return `
      <details class="room-card" data-room="${esc(r.id)}" ${isOpen ? 'open' : ''}>
        <summary class="room-card-summary">
          <span class="room-card-name">${esc(r.name)}</span>
          ${badgeHtml}
          ${photoBadge}
          ${isLandlord && isDraft ? `<button type="button" class="btn btn-ghost btn-icon" data-action="room-delete" data-id="${esc(r.id)}" aria-label="${esc(t('common.delete'))}" onclick="event.stopPropagation()">${icon('trash', { size: 'sm' })}</button>` : ''}
        </summary>
        <div class="room-card-body stack-2">
          ${photosBlock}
          ${defectRows}
          ${addDefectForm}
        </div>
      </details>`;
  }

  _roomPhotosBlock(r, photos, isLandlord, isDraft) {
    const canEdit = isLandlord && isDraft;
    if (photos.length === 0 && !canEdit) return '';
    const tiles = photos.map((p) => `
      <div class="room-photo-tile">
        <button type="button" class="defect-photo-trigger" data-action="room-photo-view" aria-label="${esc(t('handover.room_photos.view'))}" title="${esc(t('handover.room_photos.view'))}">
          <img alt="${esc(t('handover.room_photos.photo'))}" data-protected-src="/handover-room-photos/${esc(p.id)}/photo" />
          <span class="defect-photo-trigger-overlay">${icon('eye', { size: 'sm' })}</span>
        </button>
        ${canEdit ? `<button type="button" class="btn btn-ghost btn-icon room-photo-delete" data-action="room-photo-delete" data-id="${esc(p.id)}" aria-label="${esc(t('common.delete'))}" title="${esc(t('common.delete'))}">${icon('trash', { size: 'sm' })}</button>` : ''}
      </div>`).join('');
    const adder = canEdit ? `
      <label class="room-photo-add" title="${esc(t('handover.room_photos.add'))}">
        ${icon('upload', { size: 'sm' })}
        <span>${esc(t('handover.room_photos.add'))}</span>
        <input type="file" accept="image/*" hidden data-room-photo="${esc(r.id)}" />
      </label>` : '';
    return `
      <div class="room-photos">
        <div class="room-photos-head">
          <strong>${esc(t('handover.room_photos.title'))}</strong>
          <span class="faint">${esc(t('handover.room_photos.hint'))}</span>
        </div>
        <div class="room-photo-grid">${tiles}${adder}</div>
      </div>`;
  }

  _bindRooms() {
    const addRoom = this.el.querySelector('[data-form="add-room"]');
    if (addRoom) addRoom.addEventListener('submit', (ev) => {
      ev.preventDefault();
      clearFormError(addRoom);
      const name = (addRoom.querySelector('input[name="name"]').value || '').trim();
      this._addRoom(addRoom, { name });
    });
    this.el.querySelectorAll('[data-action="room-delete"]').forEach((b) => b.addEventListener('click', () => this._deleteRoom(b.getAttribute('data-id'))));
    this.el.querySelectorAll('[data-form="add-defect"]').forEach((f) => f.addEventListener('submit', (ev) => {
      ev.preventDefault();
      clearFormError(f);
      const description = (f.querySelector('input[name="description"]').value || '').trim();
      this._addDefect(f, f.getAttribute('data-room-id'), { description });
    }));
    this.el.querySelectorAll('[data-action="defect-delete"]').forEach((b) => b.addEventListener('click', () => this._deleteDefect(b.getAttribute('data-id'))));
    this.el.querySelectorAll('[data-defect-photo]').forEach((inp) => inp.addEventListener('change', () => {
      const f = inp.files && inp.files[0];
      if (f) this._uploadDefectPhoto(inp.getAttribute('data-defect-photo'), f);
    }));
    this.el.querySelectorAll('[data-action="defect-photo-view"]').forEach((b) => b.addEventListener('click', () => {
      const url = b.dataset.blobUrl || b.querySelector('img')?.src;
      const mime = b.dataset.mime || '';
      this._openPhotoLightbox(url, mime, t('handover.defects.photo'));
    }));
    this.el.querySelectorAll('[data-room-photo]').forEach((inp) => inp.addEventListener('change', () => {
      const f = inp.files && inp.files[0];
      if (f) this._uploadRoomPhoto(inp.getAttribute('data-room-photo'), f);
      inp.value = '';
    }));
    this.el.querySelectorAll('[data-action="room-photo-view"]').forEach((b) => b.addEventListener('click', () => {
      const url = b.dataset.blobUrl || b.querySelector('img')?.src;
      const mime = b.dataset.mime || '';
      this._openPhotoLightbox(url, mime, t('handover.room_photos.photo'));
    }));
    this.el.querySelectorAll('[data-action="room-photo-delete"]').forEach((b) => b.addEventListener('click', () => this._deleteRoomPhoto(b.getAttribute('data-id'))));
  }

  async _addRoom(form, body) {
    try { await api.post(`/handovers/${this.id}/rooms`, body); await this._load(); }
    catch (err) {
      const field = fieldFromApiError(err, ['name']);
      showFormError(form, err instanceof ApiError ? err.message : t('handover.errors.generic'), { field });
    }
  }

  async _deleteRoom(id) {
    if (!confirm(t('handover.rooms.delete_confirm'))) return;
    try { await api.delete(`/handover-rooms/${id}`); await this._load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : t('handover.errors.generic')); }
  }

  async _addDefect(form, roomId, body) {
    try { await api.post(`/handover-rooms/${roomId}/defects`, body); await this._load(); }
    catch (err) {
      const field = fieldFromApiError(err, ['description']);
      showFormError(form, err instanceof ApiError ? err.message : t('handover.errors.generic'), { field });
    }
  }

  async _deleteDefect(id) {
    if (!confirm(t('handover.defects.delete_confirm'))) return;
    try { await api.delete(`/handover-defects/${id}`); await this._load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : t('handover.errors.generic')); }
  }

  async _uploadDefectPhoto(id, file) {
    const fd = new FormData();
    fd.append('photo', file);
    try { await api.post(`/handover-defects/${id}/photo`, fd); await this._load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : t('handover.errors.generic')); }
  }

  async _uploadRoomPhoto(roomId, file) {
    const fd = new FormData();
    fd.append('photo', file);
    try { await api.post(`/handover-rooms/${roomId}/photos`, fd); await this._load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : t('handover.errors.generic')); }
  }

  async _deleteRoomPhoto(id) {
    if (!confirm(t('handover.room_photos.delete_confirm'))) return;
    try { await api.delete(`/handover-room-photos/${id}`); await this._load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : t('handover.errors.generic')); }
  }

  // ---------- meters ----------

  _metersBody(h, isLandlord, isDraft) {
    const meters = Array.isArray(h.meters) ? h.meters : [];
    const taken = (Array.isArray(h.readings) ? h.readings : []).reduce((acc, r) => {
      (acc[r.meter_id] = acc[r.meter_id] || []).push(r);
      return acc;
    }, {});
    if (meters.length === 0) return `<div class="empty">${esc(t('handover.meters.no_meters'))}</div>`;
    return `<div class="meter-rows">${meters.map((m) => this._meterRowHtml(m, (taken[m.id] || [])[0] || null, isDraft)).join('')}</div>`;
  }

  _meterRowHtml(m, reading, isDraft) {
    const tag = m.label ? `${m.kind} · ${m.label}` : m.kind;
    const valuePart = reading
      ? `<div class="meter-reading-value"><strong>${esc(reading.reading_value)} ${esc(m.unit_of_measure)}</strong><span class="faint"> · ${esc(reading.read_at)}</span></div>`
      : '';
    const inputForm = isDraft && !reading ? `
      <form class="meter-reading-form" data-form="reading" data-meter-id="${esc(m.id)}">
        <div data-form-error></div>
        <div class="row" style="gap:var(--space-2)">
          <input class="input grow" name="reading_value" type="number" step="0.01" min="0" placeholder="${esc(t('handover.meters.value', { uom: m.unit_of_measure }))}" />
          <input class="input" style="max-width:10rem" name="read_at" type="date" value="${new Date().toISOString().slice(0,10)}" />
          <button type="submit" class="btn btn-primary">${esc(t('handover.meters.record'))}</button>
        </div>
      </form>` : '';
    const photoSlot = reading ? this._meterPhotoSlot(reading, isDraft) : '';
    return `
      <div class="meter-row" data-meter-id="${esc(m.id)}">
        <div class="meter-row-main">
          <div class="meter-row-head">
            ${icon('package', { class: 'meter-row-icon' })}
            <div class="stack-2 grow">
              <strong>${esc(m.unit_label)}</strong>
              <span class="faint">${esc(tag)}</span>
            </div>
          </div>
          ${valuePart}
          ${inputForm}
        </div>
        ${photoSlot}
      </div>`;
  }

  _meterPhotoSlot(reading, isDraft) {
    const hasPhoto = !!reading.photo_path;
    const uploadBtn = isDraft ? `
      <label class="meter-photo-upload">
        ${icon('upload', { size: 'sm' })}
        <span>${esc(hasPhoto ? t('handover.meters.replace_photo') : t('handover.meters.attach_photo'))}</span>
        <input type="file" accept="image/*,application/pdf" hidden data-reading-photo="${esc(reading.id)}" />
      </label>` : '';
    if (hasPhoto) {
      return `
        <div class="meter-photo-slot has-photo">
          <span class="meter-photo-status">${icon('check', { size: 'sm' })}<span>${esc(t('handover.meters.photo_attached'))}</span></span>
          <button type="button" class="defect-photo-trigger meter-photo-trigger" data-action="defect-photo-view" aria-label="${esc(t('handover.defects.view_photo'))}">
            <img alt="${esc(t('handover.meters.photo_attached'))}" data-protected-src="/readings/${esc(reading.id)}/photo" />
            <span class="defect-photo-trigger-overlay">${icon('eye', { size: 'sm' })}</span>
          </button>
          ${uploadBtn}
        </div>`;
    }
    return `
      <div class="meter-photo-slot">
        <div class="meter-photo-empty">${icon('eye', { size: 'sm' })}<span class="faint">${esc(t('handover.meters.no_photo'))}</span></div>
        ${uploadBtn}
      </div>`;
  }

  _bindMeters() {
    this.el.querySelectorAll('[data-form="reading"]').forEach((f) => f.addEventListener('submit', (ev) => {
      ev.preventDefault();
      clearFormError(f);
      const value = f.querySelector('input[name="reading_value"]').value;
      const readAt = f.querySelector('input[name="read_at"]').value;
      this._addReading(f, f.getAttribute('data-meter-id'), { reading_value: value, read_at: readAt });
    }));
    this.el.querySelectorAll('[data-reading-photo]').forEach((inp) => inp.addEventListener('change', () => {
      const f = inp.files && inp.files[0];
      if (f) this._uploadReadingPhoto(inp.getAttribute('data-reading-photo'), f);
    }));
  }

  async _addReading(form, meterId, body) {
    try {
      await api.post(`/handovers/${this.id}/readings`, { meter_id: meterId, ...body });
      await this._load();
    } catch (err) {
      const field = fieldFromApiError(err, ['reading_value', 'read_at', 'meter_id']);
      showFormError(form, err instanceof ApiError ? err.message : t('handover.errors.generic'), { field });
    }
  }

  async _uploadReadingPhoto(id, file) {
    const fd = new FormData();
    fd.append('photo', file);
    try { await api.post(`/readings/${id}/photo`, fd); await this._load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : t('handover.errors.generic')); }
  }

  // ---------- deposit ----------

  _depositBody(h, isLandlord, isDraft) {
    const cur = (h.tenancy_summary && h.tenancy_summary.currency) || 'HUF';
    const expected = h.expected_deposit;
    const confirmed = h.deposit_confirmed_amount;
    const showConfirm = h.type === 'MOVE_OUT';
    if (!isLandlord || !isDraft) {
      return `<div class="stack-2">
        <div class="muted">${esc(t('handover.deposit.expected'))}: <strong>${esc(expected !== null && expected !== undefined ? formatMoney(expected, cur) : '—')}</strong></div>
        ${showConfirm ? `<div class="muted">${esc(t('handover.deposit.confirmed'))}: <strong>${esc(confirmed !== null && confirmed !== undefined ? formatMoney(confirmed, cur) : '—')}</strong></div>` : ''}
      </div>`;
    }
    return `
      <form class="stack" data-form="deposit">
        <div data-form-error></div>
        <div class="field"><label>${esc(t('handover.deposit.expected'))} (${esc(cur)})</label><input class="input" name="expected_deposit" type="number" min="0" value="${esc(expected ?? '')}" placeholder="${esc(t('handover.deposit.expected_placeholder'))}" /></div>
        ${showConfirm ? `<div class="field"><label>${esc(t('handover.deposit.confirmed'))} (${esc(cur)})</label><input class="input" name="deposit_confirmed_amount" type="number" min="0" value="${esc(confirmed ?? '')}" /></div>` : ''}
        <button type="submit" class="btn btn-outline btn-block">${esc(t('common.save'))}</button>
      </form>`;
  }

  _bindDeposit() {
    const f = this.el.querySelector('[data-form="deposit"]');
    if (!f) return;
    f.addEventListener('submit', (ev) => {
      ev.preventDefault();
      clearFormError(f);
      const body = { expected_deposit: f.querySelector('input[name="expected_deposit"]').value || null };
      const conf = f.querySelector('input[name="deposit_confirmed_amount"]');
      if (conf) body.deposit_confirmed_amount = conf.value || null;
      this._patchHeader(f, body);
    });
  }

  // ---------- signatures ----------

  _signBody(h, isLandlord, isDraft) {
    const blocks = ['LANDLORD', 'TENANT'].map((party) => this._signBlockHtml(h, party, isDraft)).join('');
    const hint = isDraft ? `<span class="faint signature-hint">${esc(t('handover.signatures.hint_shared_device'))}</span>` : '';
    return `${hint}<div class="signature-grid">${blocks}</div>`;
  }

  _signBlockHtml(h, party, isDraft) {
    const path = party === 'LANDLORD' ? h.landlord_signature_path : h.tenant_signature_path;
    const signedAt = party === 'LANDLORD' ? h.landlord_signed_at : h.tenant_signed_at;
    const partyName = party === 'LANDLORD'
      ? (h.tenancy_summary?.landlord_name || '')
      : (h.tenancy_summary?.tenant_name || '');
    const heading = `<div class="signature-block-heading">
      <strong>${esc(t('handover.signatures.' + party.toLowerCase()))}</strong>
      ${partyName ? `<span class="faint">(${esc(partyName)})</span>` : ''}
    </div>`;
    if (path) {
      return `<div class="signature-block is-signed">
        ${heading}
        <span class="faint">${esc(t('handover.signatures.signed_at', { when: formatDateTime(signedAt) }))}</span>
        <img alt="" data-protected-src="/handovers/${esc(h.id)}/signatures/${esc(party.toLowerCase())}" class="signature-image" />
      </div>`;
    }
    if (isDraft) {
      return `<div class="signature-block" data-sign-block="${esc(party)}">
        ${heading}
        <span class="faint">${esc(t('handover.signatures.awaiting'))}</span>
        <div data-sign-pad></div>
        <div class="row" style="gap:var(--space-2)">
          <button class="btn btn-ghost" data-action="sign-clear" data-party="${esc(party)}">${esc(t('handover.signatures.clear'))}</button>
          <button class="btn btn-primary grow" data-action="sign-submit" data-party="${esc(party)}">${esc(t('handover.signatures.submit'))}</button>
        </div>
      </div>`;
    }
    return `<div class="signature-block">${heading}<span class="faint">${esc(t('handover.signatures.pending'))}</span></div>`;
  }

  _bindSignatures() {
    this.el.querySelectorAll('[data-sign-block]').forEach((block) => {
      const party = block.getAttribute('data-sign-block');
      const padSlot = block.querySelector('[data-sign-pad]');
      if (padSlot) {
        const pad = new SignaturePad({ width: 480, height: 160 });
        pad.mount(padSlot);
        this._pads[party] = pad;
      }
    });
    this.el.querySelectorAll('[data-action="sign-clear"]').forEach((b) => b.addEventListener('click', () => {
      const p = b.getAttribute('data-party');
      if (this._pads[p]) this._pads[p].clear();
    }));
    this.el.querySelectorAll('[data-action="sign-submit"]').forEach((b) => b.addEventListener('click', () => this._submitSignature(b.getAttribute('data-party'))));
  }

  async _submitSignature(party) {
    const pad = this._pads[party];
    if (!pad) return;
    const blob = await pad.toBlob();
    if (!blob) { toast.error(t('handover.signatures.empty')); return; }
    const fd = new FormData();
    fd.append('signature', blob, 'signature.png');
    try {
      await api.post(`/handovers/${this.id}/signatures/${party.toLowerCase()}`, fd);
      await this._load();
      toast.success(t('handover.signatures.saved'));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('handover.errors.generic'));
    }
  }
}
